"use client";

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { FlipWords } from "./FlipBanner";

interface NavbarSearchProps {
  disabled?: boolean;
  currentFlipWord?: string;
}

export function NavbarSearch({ disabled = false }: NavbarSearchProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [showFlipWords, setShowFlipWords] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { profile } = useAuth();

  const isEmployer = profile?.role === 'employer';

  const employerFlipWords = ["titles", "expertise", "skills"];
  const jobSeekerWords = ["jobs", "companies", "challenges"];

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowFlipWords(true);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };



  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (disabled) return;
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setIsSearchFocused(false);
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        if (searchInputRef.current && !disabled) {
          searchInputRef.current.focus();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [disabled]);

  return (
    <div className="w-full max-w-md relative">
      <form
        onSubmit={onSubmit}
        className="relative w-full"
      >
        <div className="relative">
          <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
            <Search className="h-4 w-4 text-white/40" />
          </div>

          <div
            className="absolute inset-y-0 left-10 flex items-center pointer-events-none text-white/40 h-full transition-opacity duration-200"
            style={{ opacity: !searchQuery && !isSearchFocused ? 1 : 0 }}
          >
            Search for&nbsp;
            {React.useMemo(() => (
              showFlipWords ? (
                <FlipWords
                  key={isEmployer ? 'employer' : 'jobseeker'}
                  words={isEmployer ? employerFlipWords : jobSeekerWords}
                  duration={3000}
                  className="!text-white/40 !px-1"
                />
              ) : null
            ), [isEmployer, showFlipWords])}
          </div>

          <input
            ref={searchInputRef}
            type="text"
            placeholder={isSearchFocused ? "" : ""}
            value={searchQuery}
            onChange={handleChange}
            onFocus={() => setIsSearchFocused(true)}
            onBlur={() => setIsSearchFocused(false)}
            disabled={disabled}
            className={`w-full h-10 pl-10 pr-10 bg-white/5 border border-white/10 rounded-full text-white placeholder-white/40 focus:outline-none focus:border-white/20 focus:ring-1 focus:ring-white/20 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          />

        </div>

        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-white/30 pointer-events-none">
          <span className="hidden sm:inline">Ctrl+K</span>
        </div>
      </form>
    </div>
  );
}